import { Addressable } from "./addresses";

export type CalendarBlockFilters = {
  visitTypeIds: number[] | null;
} | null;

export const calendarBlockTypes = ['break', 'meeting', 'facility', 'visit_type'] as const;
export type CalendarBlockType = typeof calendarBlockTypes[number];

export const defaultCalendarBlockTitles: Record<CalendarBlockType, string> = {
  break: "Break",
  meeting: "Meeting",
  facility: "Facility Visit",
  visit_type: "Visit Types",
}

export type GenericCalendarBlock = {
  addressStreet: string | null;
  addressLineTwo: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressZip: string | null;
  addressPlaceId?: string | null;
  addressCoordinates: string | null;
  addressTimezone: string | null;
  providerId: number;
  startTime: Date;
  endTime: Date;
  title: string | null;
  eventStatus: "scheduled" | "canceled";
  startOfShiftDriveTime: number;
  interVisitDriveTime: number;
  endOfShiftDriveTime: number;
  filters: CalendarBlockFilters;
  blockType: CalendarBlockType;
}

export enum CalendarBlockConflict {
  ALLOWED = 'allowed',
  NO_VISITS_ALLOWED = 'noVisitsAllowed',
  WRONG_VISIT_TYPE = 'wrongVisitType',
  WRONG_ADDRESS = 'wrongAddress',
}

const CALENDAR_BLOCKS_WITH_FILTERS = ["visit_type", "facility"];
const CALENDAR_BLOCKS_WITH_ADDRESS = ["meeting", "facility"];

export const blockFieldInfo = (blockType: CalendarBlockType) => ({
  showVisitType: CALENDAR_BLOCKS_WITH_FILTERS.includes(blockType),
  showAddress: CALENDAR_BLOCKS_WITH_ADDRESS.includes(blockType),

  requiredAddress: blockType ===  "facility",
  shouldRequireVisitTypes: blockType === "visit_type",
})

export const isAllowedInBlock = (visitTypeId: number, address: Addressable, block: GenericCalendarBlock): CalendarBlockConflict => {
  if(!calendarBlockIsFilter(block)) return CalendarBlockConflict.NO_VISITS_ALLOWED;
  if(calendarBlockLocationFilterMismatch(block, address)) return CalendarBlockConflict.WRONG_ADDRESS
  if(calendarBlockVisitTypeFilterMismatch(block, visitTypeId)) return CalendarBlockConflict.WRONG_VISIT_TYPE
  return CalendarBlockConflict.ALLOWED
}

export const calendarBlockIsFilter = (block: {filters: CalendarBlockFilters}): boolean => {
  return block.filters !== null;
};

export const getDefaultFilters = (blockType: CalendarBlockType): CalendarBlockFilters => {
  switch (blockType) {
    case 'break':
      return null;
    case 'meeting':
      return null;
    case 'facility':
      return { visitTypeIds: null };
    case 'visit_type':
      return { visitTypeIds: [] };
    default:
      return null;
  }
}

export const calendarBlockVisitTypeFilterMismatch = (
  block: GenericCalendarBlock,
  visitTypeId: number
): boolean => {
  // skip filter if not set
  if (!block.filters?.visitTypeIds?.length) return false;
  return !block.filters.visitTypeIds.includes(visitTypeId);
};

export const calendarBlockLocationFilterMismatch = (
  block: GenericCalendarBlock,
  patientAddress: Addressable
): boolean => {
  // skip the filter if address not set on block
  if (!block.addressStreet) return false;
  
  const normalize = (s?: string | null): string =>
    (s ?? "").trim().toLowerCase();

  const fields: (keyof Addressable)[] = [
    'addressStreet',
    // addressLineTwo skipped because we want to treat rooms / apartments / etc as the same address
    'addressCity',
    'addressState',
    'addressZip',
  ];

  // Return true (mismatch) if any field is missing/blank OR any field doesn't match
  return fields.some((key) => {
    const a = normalize(block[key]);
    const b = normalize(patientAddress[key]);
    return a === '' || b === '' || a !== b;
  });
};

export const isCalendarBlockIdleTime = (blockType: string) => {
  return blockType === "break";
}

export const isCalendarBlockFilters = (value: unknown): value is CalendarBlockFilters => {
  if (value === null) return true;
  if (typeof value !== "object" || Array.isArray(value)) return false;
  const obj = value as Record<string, unknown>;

  if (!("visitTypeIds" in obj)) return true; // optional
  const ids = obj.visitTypeIds;
  if (ids === null) return true;
  if (!Array.isArray(ids)) return false;
  return ids.every((id) => typeof id === "number");
}
